<script setup>
import { ref, provide, computed, onMounted, watchEffect, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'

import BottomNav from '@/components/BottomNav.vue'
import AppHeader from '@/components/AppHeader.vue'
import AuthModal from '@/components/auth/AuthModal.vue'
import { useDefaultHead } from '@/composables/useDefaultHead'
import ToastContainer from '@/components/ToastContainer.vue'
import ConsentBanner from '@/components/ConsentBanner.vue'
import UpgradeTrialModal from '@/components/upgrade/UpgradeTrialModal.vue'
import { useAuth } from '@/composables/useAuth'
import { useAuthModalAutoOpen } from '@/composables/useAuthModalAutoOpen.js'

useDefaultHead()

// Hide splash screen once Vue app is fully mounted
// Ensure minimum display time of 2 seconds
const appStartTime = Date.now()
const MIN_SPLASH_DURATION = 2000 // 2 seconds
const isNativePlatform = Capacitor.isNativePlatform()

onMounted(async () => {
  if (!isNativePlatform) return

  const elapsedTime = Date.now() - appStartTime
  const remainingTime = MIN_SPLASH_DURATION - elapsedTime
  
  if (remainingTime > 0) {
    // Wait for the remaining time to reach minimum duration
    await new Promise(resolve => setTimeout(resolve, remainingTime))
  }
  
  await SplashScreen.hide()
})

const route = useRoute()
const showAuth = ref(false)
const authMessage = ref('')
const authInitialTab = ref('login')
const { isAuthenticated } = useAuth()

// The unobtrusive web bar is fixed to the bottom of the screen, so it comes last
// in the DOM too: keyboard users reach the page's call to action before the
// consent controls, which stay fully reachable after it. The native bottom sheet
// dims and blocks the page, so it keeps its place first in the DOM.
const consentAfterContent = computed(() => !isNativePlatform && !!route.meta?.consentUnobtrusive)

const GUEST_FREE_MEAL_PLAN_CLASS = 'guest-free-meal-plan'
const UPGRADE_ROUTE_CLASS = 'upgrade-route'
const NATIVE_PLATFORM_CLASS = 'native-platform'

const isFreeMealPlanRoute = computed(() => route.name === 'FreeMealPlan')

/**
 * Meal-plan funnel rendered with no app chrome (no header/footer/bottom nav), so
 * the chrome padding reserved on <body> must be dropped or the page scrolls. This
 * is the dedicated FreeMealPlan route for everyone (authenticated included), plus
 * the Planner route for guests (which embeds FreeMealPlan). Authenticated Planner
 * keeps its bottom nav, so it is excluded.
 */
const isChromelessMealPlanFunnel = computed(() =>
  isFreeMealPlanRoute.value || (!isAuthenticated.value && route.name === 'Planner')
)

const shouldShowHeaderFooter = computed(() => {
  if (route.meta.hideHeaderFooter) return false
  if (isFreeMealPlanRoute.value) return false
  if (isChromelessMealPlanFunnel.value) return false
  return true
})

const shouldShowBottomNav = computed(() => {
  if (route.meta.hideHeaderFooter) return false
  if (isFreeMealPlanRoute.value) return false
  if (isAuthenticated.value && route.name === 'Planner') return true
  if (route.meta.hideBottomNav) return false
  return true
})

watchEffect(() => {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle(GUEST_FREE_MEAL_PLAN_CLASS, isChromelessMealPlanFunnel.value)
  document.documentElement.classList.toggle(UPGRADE_ROUTE_CLASS, route.path === '/upgrade')
  document.documentElement.classList.toggle(NATIVE_PLATFORM_CLASS, isNativePlatform)
})

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return
  document.documentElement.classList.remove(GUEST_FREE_MEAL_PLAN_CLASS)
  document.documentElement.classList.remove(UPGRADE_ROUTE_CLASS)
  document.documentElement.classList.remove(NATIVE_PLATFORM_CLASS)
})

function openAuthModal (message = '', initialTab = 'login') {
  authMessage.value = message
  authInitialTab.value = initialTab
  showAuth.value = true
}

provide('openAuthModal', openAuthModal)
useAuthModalAutoOpen({ showAuth, isAuthenticated, openAuthModal, initialTab: 'login' })
</script>

<template>
  <ToastContainer/>
  <ConsentBanner v-if="!consentAfterContent"/>
  <UpgradeTrialModal/>
  <AppHeader v-if="shouldShowHeaderFooter" @show-login="openAuthModal()"/>
  <AuthModal
v-model:visible="showAuth" :initial-tab="authInitialTab" :message="authMessage"
             @authenticated="showAuth = false"/>
  <!-- flex-1 + min-h-0 so route roots span the full #app width/height (full-bleed pages rely on this) -->
  <div class="flex w-full min-w-0 flex-1 flex-col self-stretch min-h-0">
    <!--
      No transition on route changes, deliberately.

      With `mode="out-in"` the incoming page only mounts once the outgoing one
      has finished leaving, and that leave was observed hanging: the element
      kept `fade-leave-from` indefinitely, so nothing replaced it and the
      visitor sat on a blank page until they reloaded. It stranded people
      crossing from the campaign landing page into the planner.

      Dropping the mode fixed the blank page but left the stalled page stacked
      behind the new one. A cross-fade between routes is decoration; arriving
      reliably is not. Removed until it can be made to behave.
    -->
    <RouterView />
  </div>
  <BottomNav v-if="shouldShowBottomNav"/>
  <ConsentBanner v-if="consentAfterContent"/>
</template>
